import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Stack,
  Card,
} from '@mui/material';
import {
  AssignmentTurnedInOutlined as AcceptanceIcon,
  BusinessCenterOutlined as ServicesIcon,
  FactCheckOutlined as ResponsibilitiesIcon,
  PaymentsOutlined as PaymentIcon,
  CopyrightOutlined as IpIcon,
  GavelOutlined as LiabilityIcon,
  AccountBalanceOutlined as LawIcon,
  EmailOutlined as ContactIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  LanguageOutlined as WebIcon,
  LocationOnOutlined as LocationIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

const termsSections = [
  { id: 'acceptance-of-terms', title: '1. Acceptance of Terms', icon: <AcceptanceIcon sx={{ color: '#C59B27' }} /> },
  { id: 'services-offered', title: '2. Services Offered', icon: <ServicesIcon sx={{ color: '#C59B27' }} /> },
  { id: 'client-responsibilities', title: '3. Client Responsibilities', icon: <ResponsibilitiesIcon sx={{ color: '#C59B27' }} /> },
  { id: 'fees-payment', title: '4. Fees & Payment Terms', icon: <PaymentIcon sx={{ color: '#C59B27' }} /> },
  { id: 'intellectual-property', title: '5. Intellectual Property', icon: <IpIcon sx={{ color: '#C59B27' }} /> },
  { id: 'limitation-of-liability', title: '6. Limitation of Liability', icon: <LiabilityIcon sx={{ color: '#C59B27' }} /> },
  { id: 'governing-law', title: '7. Governing Law (India)', icon: <LawIcon sx={{ color: '#C59B27' }} /> },
  { id: 'contact-information', title: '8. Contact Information', icon: <ContactIcon sx={{ color: '#C59B27' }} /> },
];

const TermsOfService: React.FC = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <SEOHead
        title="Terms of Service | Fingard Partners"
        description="Review the terms and conditions for using Fingard Partners services."
        keywords="terms of service, terms and conditions, Fingard Partners, CA services agreement, legal terms, India governing law"
      />

      {/* Hero Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0B2847 0%, #123D6B 60%, #081E35 100%)',
          color: '#FFFFFF',
          pt: { xs: 8, md: 10 },
          pb: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          fontFamily: '"Poppins", sans-serif',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 850, mx: 'auto', textAlign: 'center' }}>
            <Chip
              icon={<AcceptanceIcon sx={{ '&&': { color: '#C59B27', fontSize: 18 } }} />}
              label="Legal Agreement & Service Terms"
              sx={{
                backgroundColor: 'rgba(197, 155, 39, 0.15)',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '0.85rem',
                letterSpacing: '0.04em',
                px: 1.5,
                py: 0.5,
                mb: 2.5,
                borderRadius: 3,
                border: '1px solid rgba(197, 155, 39, 0.4)',
                fontFamily: '"Poppins", sans-serif',
              }}
            />

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 2.5,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Terms of Service
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#E2E8F0',
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.15rem' },
                lineHeight: 1.7,
                maxWidth: 750,
                mx: 'auto',
                mb: 3,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              These Terms of Service govern the engagement, provision of Chartered Accountancy, taxation, and corporate compliance advisory services by Fingard Partners LLP.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Chip
                label="Company: Fingard Partners LLP"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
              <Chip
                label="Governing Law: Republic of India"
                sx={{
                  backgroundColor: 'rgba(197, 155, 39, 0.2)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(197, 155, 39, 0.4)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
              <Chip
                label="Effective Date: August 2026"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Content Body */}
      <Box sx={{ py: { xs: 6, md: 9 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Quick Navigation Table of Contents */}
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ position: 'sticky', top: 100 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 4px 20px rgba(11, 40, 71, 0.04)',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontSize: '0.8rem',
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Contract Sections
                  </Typography>
                  <List disablePadding>
                    {termsSections.map((sec) => (
                      <ListItem
                        key={sec.id}
                        button
                        onClick={() => scrollToSection(sec.id)}
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderRadius: 2,
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(18, 61, 107, 0.06)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>{sec.icon}</ListItemIcon>
                        <ListItemText
                          primary={sec.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#102A43',
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2.5 }} />

                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(18, 61, 107, 0.04)' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        color: '#0B2847',
                        mb: 0.5,
                        fontFamily: '"Poppins", sans-serif',
                      }}
                    >
                      Questions Regarding Terms?
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#486581',
                        mb: 1.5,
                        fontFamily: '"Poppins", sans-serif',
                      }}
                    >
                      Our corporate legal advisory desk is available to clarify any engagement stipulations.
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: '#123D6B',
                        color: '#123D6B',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        fontFamily: '"Poppins", sans-serif',
                        '&:hover': {
                          borderColor: '#0B2847',
                          backgroundColor: 'rgba(18, 61, 107, 0.06)',
                        },
                      }}
                    >
                      Contact Advisory Desk
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Grid>

            {/* Right Terms Content */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 5, md: 6 },
                  borderRadius: 3,
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 4px 20px rgba(11, 40, 71, 0.04)',
                }}
              >
                {/* 1. Acceptance of Terms */}
                <Box id="acceptance-of-terms" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <AcceptanceIcon sx={{ color: '#C59B27' }} /> 1. Acceptance of Terms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    By accessing, browsing, registering on, or using the website <strong>fingard.co</strong>, digital tools, tax calculation engines, or retaining the professional services of <strong>Fingard Partners LLP</strong> (&quot;Fingard&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;Firm&quot;), you (&quot;Client&quot;, &quot;User&quot;, or &quot;You&quot;) agree to be legally bound by these Terms of Service.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    If you are entering into this agreement on behalf of a company, partnership, LLP, trust, or other legal entity, you represent and warrant that you possess full legal authority to bind such entity to these Terms.
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 2. Services Offered */}
                <Box id="services-offered" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <ServicesIcon sx={{ color: '#C59B27' }} /> 2. Services Offered
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Fingard Partners LLP provides specialized professional advisory, accounting, compliance, and taxation services across India, including but not limited to:
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      { title: 'Direct & International Taxation', desc: 'Income Tax Return (ITR) preparation & filing, advance tax computation, capital gains planning, NRI 15CA/CB certificates, and assessment representations.' },
                      { title: 'Indirect Taxation (GST)', desc: 'GST registration, monthly reconciliation (GSTR-1, GSTR-3B, GSTR-9), ITC audits, refund processing, and department notice assistance.' },
                      { title: 'Corporate & MCA Compliance', desc: 'Company incorporation (Private Limited, OPC, Section 8), LLP registration, annual ROC filings (AOC-4, MGT-7), Director KYC, and secretarial documentation.' },
                      { title: 'Startup & Virtual CFO Advisory', desc: 'DPIIT startup recognition, cap table management, business valuation, payroll structuring, and ongoing outsourced financial controllership.' },
                    ].map((srv, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderColor: '#E2E8F0', p: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B2847', mb: 0.5, fontFamily: '"Poppins", sans-serif' }}>
                            {srv.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#486581', fontSize: '0.85rem', lineHeight: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                            {srv.desc}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 3. Client Responsibilities */}
                <Box id="client-responsibilities" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <ResponsibilitiesIcon sx={{ color: '#C59B27' }} /> 3. Client Responsibilities
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    To ensure the timely and accurate execution of your statutory filings and advisory mandates, you agree to:
                  </Typography>
                  <List disablePadding>
                    {[
                      'Furnish accurate, authentic, and complete financial statements, tax records (Form 16, 26AS, AIS, bank statements), and identity proofs.',
                      'Provide requisite authorizations, Digital Signature Certificates (DSC), OTPs, and statutory login credentials in a timely manner.',
                      'Review draft computations, returns, and submissions thoroughly prior to granting formal filing approval.',
                      'Acknowledge that Fingard Partners LLP will not be held liable for statutory penalties or interest arising from incomplete, delayed, or misleading data supplied by the client.',
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.8 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon sx={{ color: '#C59B27', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            color: '#486581',
                            lineHeight: 1.6,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 4. Fees & Payment */}
                <Box id="fees-payment" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <PaymentIcon sx={{ color: '#C59B27' }} /> 4. Fees & Payment
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Professional fees for our services are defined in our formal engagement proposals or on our website pricing schedules.
                  </Typography>
                  <Box sx={{ p: 3, backgroundColor: 'rgba(18, 61, 107, 0.04)', borderRadius: 2.5, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B2847', mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                      Payment Policies:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>Government Filing Charges:</strong> Professional fees exclude statutory government fees, ROC challans, name approval fees, stamp duties, or portal processing charges, which shall be borne directly by the client.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>Taxes:</strong> All fees are subject to applicable Goods and Services Tax (GST) as per prevailing Indian tax statutes.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>Refund Policy:</strong> Once professional advisory has commenced or statutory returns have been filed, fees paid are strictly non-refundable due to the specialized professional time and resources incurred.
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 5. Intellectual Property */}
                <Box id="intellectual-property" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <IpIcon sx={{ color: '#C59B27' }} /> 5. Intellectual Property
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    All content, algorithms, tax calculators, financial models, design assets, and brand trademarks on <strong>fingard.co</strong> remain the exclusive intellectual property of Fingard Partners LLP.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.7,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Upon full settlement of advisory invoices, clients are granted a perpetual, non-exclusive license to utilize final deliverables, filing acknowledgements, computation statements, and corporate reports for their internal lawful business operations.
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 6. Limitation of Liability */}
                <Box id="limitation-of-liability" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <LiabilityIcon sx={{ color: '#C59B27' }} /> 6. Limitation of Liability
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    To the maximum extent permitted by applicable Indian law:
                  </Typography>
                  <List disablePadding>
                    {[
                      'Fingard Partners LLP exercises reasonable professional skill and diligence consistent with ICAI standards; however, advisory outcomes are subject to regulatory assessments and statutory discretion of tax and MCA authorities.',
                      'The Firm shall not be liable for delayed filings or system rejections resulting from external Government portal downtimes (e.g., e-Filing Income Tax portal, GSTN portal, or MCA21 network outages).',
                      'In no event shall Fingard Partners LLP or its partners be liable for indirect, punitive, or consequential damages. Our aggregate total liability for any claim shall not exceed the actual professional fees paid by the client for that specific engagement.',
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.8 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon sx={{ color: '#C59B27', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            color: '#486581',
                            lineHeight: 1.6,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 7. Governing Law */}
                <Box id="governing-law" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <LawIcon sx={{ color: '#C59B27' }} /> 7. Governing Law (India)
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    These Terms of Service and any professional engagement agreements shall be governed by, interpreted, and construed in accordance with the <strong>Laws of the Republic of India</strong>, without regard to conflict of law principles.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.7,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Any legal suit, action, or proceeding arising out of or related to these Terms shall be instituted exclusively in the competent courts having jurisdiction in India.
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* 8. Contact Information */}
                <Box id="contact-information" sx={{ scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <ContactIcon sx={{ color: '#C59B27' }} /> 8. Contact Information
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 3,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    For inquiries, contract clarifications, or formal notices regarding these Terms of Service, please reach out to our corporate advisory team:
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0B2847', mb: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                      Fingard Partners LLP
                    </Typography>
                    <Stack spacing={1.2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <WebIcon sx={{ color: '#123D6B', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#102A43', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                          Website: <a href="https://fingard.co" target="_blank" rel="noopener noreferrer" style={{ color: '#123D6B', textDecoration: 'underline' }}>fingard.co</a>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmailIcon sx={{ color: '#123D6B', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#102A43', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                          Legal & Engagement Email: <a href="mailto:coo.fingard@gmail.com" style={{ color: '#123D6B', textDecoration: 'none' }}>coo.fingard@gmail.com</a>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationIcon sx={{ color: '#123D6B', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#102A43', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                          Jurisdiction: Republic of India
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                      <Button
                        component={RouterLink}
                        to="/contact"
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          backgroundColor: '#123D6B',
                          color: '#FFFFFF',
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif',
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: '#0B2847',
                          },
                        }}
                      >
                        Contact Advisory Services
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default TermsOfService;
